/**
 * Converts a name to a URL-safe slug.
 * e.g. "My Team!" → "my-team"
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;

const SCORE_THRESHOLDS = { success: 80, warning: 50 } as const;

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * Formats a timestamp as a human-readable relative time string.
 * e.g. "just now", "5 minutes ago", "2 hours ago"
 */
export function formatRelativeTime(timestamp: number): string {
  const elapsed = Date.now() - timestamp;

  if (elapsed < MINUTE) return 'just now';

  const units: Array<[number, string]> = [
    [MONTH, 'month'],
    [WEEK, 'week'],
    [DAY, 'day'],
    [HOUR, 'hour'],
    [MINUTE, 'minute'],
  ];

  for (const [threshold, unit] of units) {
    if (elapsed >= threshold) {
      const count = Math.floor(elapsed / threshold);
      return `${count} ${unit}${count !== 1 ? 's' : ''} ago`;
    }
  }

  return 'just now';
}

/**
 * Formats a timestamp to a readable date like "Mar 2, 2026".
 */
export function formatDate(timestamp: number): string {
  return dateFormatter.format(new Date(timestamp));
}

/**
 * Formats a duration in milliseconds to a compact string.
 * Under 1s: "450ms". Over 1s: "1.2s".
 */
export function formatDuration(ms: number): string {
  if (ms < SECOND) return `${Math.round(ms)}ms`;
  return `${(ms / SECOND).toFixed(1)}s`;
}

/**
 * Maps a numeric score to a labeled color band.
 */
export function formatScore(score: number): {
  label: string;
  color: 'success' | 'warning' | 'error';
} {
  const color =
    score >= SCORE_THRESHOLDS.success
      ? 'success'
      : score >= SCORE_THRESHOLDS.warning
        ? 'warning'
        : 'error';

  return { label: String(score), color };
}
